import { useState } from 'react'

import ConfirmDeleteGroupModal from './ConfirmDeleteGroupModal'
import {
  PlanGroupsDocument,
  useDeletePlanGroupMutation,
  useDeleteTemplateGroupMutation,
  useDeletePlanItemMutation,
  useGroupByIdQuery
} from '../../../../generated/graphql'
import useModal from '../../../../lib/hooks/useModal'
import { updateDeletePlanGroupCache } from '../../../../lib/cacheUtils'
import useNotification from '../../../../lib/hooks/useNotification'
import { useAuth } from '../../../provider/OIDCProvider'
import { userIsOnlyStudent } from '../../../../lib/authenticationUtils'

type ConfirmDeleteGroupModalWrapperProps = {
  /** ID of Plan Group */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** Indicates if this is a template */
  isTemplate: boolean
  /** ID of the plan/template */
  planId: string
}

const ConfirmDeleteGroupModalWrapper = ({
  id,
  isOpen,
  isTemplate,
  planId
}: ConfirmDeleteGroupModalWrapperProps) => {
  const { showErrorNotif } = useNotification()
  const { user } = useAuth()
  const { hideModal } = useModal()

  const [isDeleting, setIsDeleting] = useState(false)
  const [deletionProgress, setDeletionProgress] = useState<string | undefined>(undefined)

  const query = useGroupByIdQuery({
    variables: { id },
    onError: () => {
      showErrorNotif('An error occurred. Please refresh the page.')
    }
  })

  const [deletePlanItem] = useDeletePlanItemMutation({
    onError: () => {
      // Error handling done in the deletion loop
    }
  })

  const [deleteGroup] = useDeletePlanGroupMutation({
    variables: { input: id },
    update: (cache) => updateDeletePlanGroupCache(cache, id),
    onError: () => showErrorNotif('Plan group could not be deleted.', deleteGroup),
    optimisticResponse: {
      __typename: 'Mutation',
      deletePlanGroup: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })

  const [deleteTemplateGroup] = useDeleteTemplateGroupMutation({
    variables: { input: id },
    update: (cache) => updateDeletePlanGroupCache(cache, id),
    refetchQueries: [
      // Because of the re-sequencing that happens when a group is deleted, refetch groups
      // so the titles are accurate.  The logic to re-sequence is complicated
      // enough that it should stay in the backend.
      {
        query: PlanGroupsDocument,
        variables: { planId }
      }
    ],
    onError: () => showErrorNotif('Template group could not be deleted.', deleteTemplateGroup),
    optimisticResponse: {
      __typename: 'Mutation',
      deleteTemplateGroup: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })

  if (query.loading) {
    return null
  }

  const items = query.data?.planGroup.items || []
  const itemCount = items.length
  const lockedItems = items.filter((item) => item.locked)
  const lockedItemCount = lockedItems.length

  // Check if user is student (cannot delete locked items)
  const isStudent = userIsOnlyStudent(user)
  const userCanDeleteLocked = !isStudent

  const isDisabled = itemCount > 0

  // Handler for deleting group without items (original behavior)
  const onConfirm = () => {
    if (isTemplate) {
      deleteTemplateGroup()
    } else {
      deleteGroup()
    }
    hideModal()
  }

  // Handler for deleting group with all items
  const onConfirmWithItems = async () => {
    setIsDeleting(true)

    try {
      // Delete items sequentially to ensure audit entries are created for each
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i]
        setDeletionProgress(`Deleting item ${i + 1} of ${items.length}...`)

        try {
          // eslint-disable-next-line no-await-in-loop
          await deletePlanItem({
            variables: { input: item.id }
          })
        } catch (error) {
          // If an item fails to delete, stop and show error
          setIsDeleting(false)
          setDeletionProgress(undefined)
          showErrorNotif(
            `Failed to delete item ${i + 1} of ${
              items.length
            }. Please try again or contact support.`
          )
          return
        }
      }

      // All items deleted successfully, now delete the group
      setDeletionProgress('Deleting term...')

      if (isTemplate) {
        await deleteTemplateGroup()
      } else {
        await deleteGroup()
      }

      // Success! Close modal
      setIsDeleting(false)
      setDeletionProgress(undefined)
      hideModal()
    } catch (error) {
      // Group deletion failed
      setIsDeleting(false)
      setDeletionProgress(undefined)
      showErrorNotif('Items were deleted but the term could not be deleted. Please try again.')
    }
  }

  return (
    <ConfirmDeleteGroupModal
      isDisabled={isDisabled}
      itemCount={itemCount}
      lockedItemCount={lockedItemCount}
      userCanDeleteLocked={userCanDeleteLocked}
      isOpen={isOpen}
      isDeleting={isDeleting}
      deletionProgress={deletionProgress}
      onConfirm={onConfirm}
      onConfirmWithItems={onConfirmWithItems}
    />
  )
}

export default ConfirmDeleteGroupModalWrapper
